import { useOfficialStore } from '$/store/useOfficialStore';
import { getUrlSearchParams } from '$/utils';
import { mtstatTrack } from '$/utils/mtstat';

export type TeamProjectCreateLocation =
  | 'team_add'
  | 'no_project'
  | 'create_copy'
  | 'personal_upload'
  | 'project_share'
  | 'room_bar';

export type TeamConversationCreateLocation =
  | TeamProjectCreateLocation
  | 'room_bar_more';

interface TeamTrackingContextOverrides {
  projectId?: string | number | null;
  roomId?: string | number | null;
  oid?: string | number | null;
}

interface TeamTrackingContext {
  project_id: string;
  room_id: string;
  oid: string;
}

interface TrackTeamProjectCreatedOptions extends TeamTrackingContextOverrides {
  location: TeamProjectCreateLocation;
  folderName?: string;
}

interface TrackTeamConversationCreatedOptions extends TeamTrackingContextOverrides {
  location: TeamConversationCreateLocation;
  folderName?: string;
  type?: string;
}

interface TrackTeamShareOptions extends TeamTrackingContextOverrides {
  location?: string;
  shareSys?: string;
}

export type TeamWaitEditShowState = 'being_used' | 'release';

interface TrackTeamWaitEditShowOptions extends TeamTrackingContextOverrides {
  state: TeamWaitEditShowState;
  inUseId?: string | number | null;
}

interface TrackTeamWaitEditClickOptions extends TeamTrackingContextOverrides {
  inUseId?: string | number | null;
}

export type TeamHistoryCardMediaType = 'file' | 'image' | 'video' | 'audio';

interface TrackTeamHistoryCardOptions extends TeamTrackingContextOverrides {
  cardId: string | number;
  positionId: number;
  tabId: string;
  tabName: string;
  nodeName?: string | number | null;
  nodeNum?: string | number | null;
  authorId?: string | number | null;
  mediaType: TeamHistoryCardMediaType;
  mediaUrl?: string | null;
}

interface TrackTeamOidCreateOptions {
  oid: string | number;
  oidName: string;
  teamNum: number;
}

interface TrackTeamUploadDialogOptions {
  projectIds: Array<string | number | null | undefined>;
  roomIds: Array<string | number | null | undefined>;
  urls: Array<string | null | undefined>;
  oid?: string | number | null;
}

/** 将埋点上下文字段统一转换为字符串，空值按空字符串上报。 */
function normalizeTeamTrackValue(value: string | number | null | undefined): string {
  return value == null ? '' : String(value).trim();
}

/** 判断当前是否为团队版（存在 clientOId）。 */
export function isTeamEditionTracking(): boolean {
  return Boolean(useOfficialStore.getState().clientOId);
}

/** 从显式参数、当前团队和 URL 中解析团队埋点公共上下文。 */
export function resolveTeamTrackingContext(
  overrides: TeamTrackingContextOverrides = {},
): TeamTrackingContext {
  const searchParams = typeof window === 'undefined'
    ? new URLSearchParams()
    : getUrlSearchParams();
  const oid = normalizeTeamTrackValue(
    overrides.oid ?? useOfficialStore.getState().clientOId,
  );
  return {
    project_id: normalizeTeamTrackValue(
      overrides.projectId ?? searchParams.get('project_id'),
    ),
    room_id: normalizeTeamTrackValue(
      overrides.roomId ?? searchParams.get('room_id'),
    ),
    oid: oid === '0' ? '' : oid,
  };
}

/**
 * 团队版埋点附带 oid；个人版不上报 oid 字段。
 */
function appendOidForTeamEdition(
  payload: Record<string, string>,
  oid: string,
): Record<string, string> {
  if (isTeamEditionTracking() && oid) {
    return { ...payload, oid };
  }
  return payload;
}

/** 判断 location 是否属于团队项目创建事件约定的枚举。 */
export function isTeamProjectCreateLocation(
  location: string,
): location is TeamProjectCreateLocation {
  return [
    'team_add',
    'no_project',
    'create_copy',
    'personal_upload',
    'project_share',
    'room_bar',
  ].includes(location);
}

/** Team Studio 内建项目时使用的 location，埋点由页面组件自行上报。 */
export function isTeamStudioInternalTrackLocation(location: string): boolean {
  return location === 'room_bar' || location === 'room_bar_more';
}

/** 将首页等入口 location 映射为大禹约定的团队项目创建 location。 */
export function resolveTeamProjectCreateLocation(
  location: string,
): TeamProjectCreateLocation | null {
  if (isTeamProjectCreateLocation(location)) {
    return location;
  }
  if (location === 'home_page_bar' || location === 'home_personal_space') {
    return 'team_add';
  }
  return null;
}

/** 分享访问范围映射为大禹 share_sys 枚举。 */
export function mapTeamShareScopeToShareSys(
  scope: 'team' | 'public',
): 'team_user' | 'all_user' {
  return scope === 'public' ? 'all_user' : 'team_user';
}

/** 项目 ID 返回后上报团队项目创建事件。 */
export function trackTeamProjectCreated({
  location,
  folderName = '/',
  ...contextOverrides
}: TrackTeamProjectCreatedOptions): void {
  if (!isTeamEditionTracking()) return;
  const context = resolveTeamTrackingContext(contextOverrides);
  if (!context.project_id) return;
  mtstatTrack('roboneo_team_project_create', appendOidForTeamEdition({
    location,
    room_id: context.room_id,
    project_id: context.project_id,
    folder_name: folderName || '/',
  }, context.oid));
}

/** 房间 ID 返回后上报新建会话事件。 */
export function trackTeamConversationCreated({
  location,
  folderName = '/',
  type = 'agent_teams',
  ...contextOverrides
}: TrackTeamConversationCreatedOptions): void {
  const context = resolveTeamTrackingContext(contextOverrides);
  if (!context.room_id) return;
  mtstatTrack('create_conversation', appendOidForTeamEdition({
    project_id: context.project_id,
    folder_name: folderName || '/',
    type,
    room_id: context.room_id,
    location,
  }, context.oid));
}

/** 团队项目删除接口返回后上报成功或失败状态。 */
export function trackTeamProjectDeleted(
  projectId: string | number,
  state: 'success' | 'fail',
): void {
  const context = resolveTeamTrackingContext({ projectId });
  if (!context.project_id) return;
  mtstatTrack('roboneo_team_project_delete', appendOidForTeamEdition({
    project_id: context.project_id,
    state,
  }, context.oid));
}

/** 用户切换到目标项目时上报切换事件。 */
export function trackTeamProjectSwitch(
  targetProjectId: string | number,
  roomId: string | number,
): void {
  const context = resolveTeamTrackingContext({ roomId });
  const payload: Record<string, string> = {
    target_oid: String(targetProjectId),
    room_id: context.room_id,
  };
  if (isTeamEditionTracking() && context.oid) {
    payload.cur_oid = context.oid;
  }
  mtstatTrack('roboneo_team_project_switch', payload);
}

/** 分享弹窗曝光。 */
export function trackTeamShareShow({
  location = '',
  ...contextOverrides
}: TrackTeamShareOptions = {}): void {
  const context = resolveTeamTrackingContext(contextOverrides);
  if (!context.room_id) return;
  mtstatTrack('roboneo_team_share_show', appendOidForTeamEdition({
    room_id: context.room_id,
    project_id: context.project_id,
    ...(location ? { location } : {}),
  }, context.oid));
}

/** 分享弹窗内确认复制链接触发。 */
export function trackTeamShareClick({
  location = '',
  shareSys,
  ...contextOverrides
}: TrackTeamShareOptions = {}): void {
  if (!isTeamEditionTracking()) return;
  const context = resolveTeamTrackingContext(contextOverrides);
  if (!context.room_id) return;
  mtstatTrack('roboneo_team_share_click', appendOidForTeamEdition({
    room_id: context.room_id,
    project_id: context.project_id,
    ...(location ? { location } : {}),
    ...(shareSys ? { share_sys: shareSys } : {}),
  }, context.oid));
}

/** 画布被占用/释放等待编辑蒙层曝光（团队版他人占用 + 个人版/页签冲突自占）。 */
export function trackTeamWaitEditShow({
  state,
  inUseId,
  ...contextOverrides
}: TrackTeamWaitEditShowOptions): void {
  const context = resolveTeamTrackingContext(contextOverrides);
  if (!context.room_id) return;
  mtstatTrack('roboneo_team_wait_edit_show', appendOidForTeamEdition({
    room_id: context.room_id,
    project_id: context.project_id,
    state,
    in_use_id: normalizeTeamTrackValue(inUseId),
  }, context.oid));
}

/** 等待编辑蒙层点击（仅个人项目：页签冲突「在当前页编辑」/ 释放后「刷新页面」）。 */
export function trackTeamWaitEditClick({
  inUseId,
  ...contextOverrides
}: TrackTeamWaitEditClickOptions): void {
  if (isTeamEditionTracking()) return;
  const context = resolveTeamTrackingContext(contextOverrides);
  if (!context.room_id) return;
  mtstatTrack('roboneo_team_wait_edit_click', {
    room_id: context.room_id,
    project_id: context.project_id,
    in_use_id: normalizeTeamTrackValue(inUseId),
  });
}

/** 组装团队历史卡片曝光/点击公共参数。 */
function buildTeamHistoryCardTrackPayload(
  options: TrackTeamHistoryCardOptions,
): Record<string, string> | null {
  if (!isTeamEditionTracking()) return null;
  const context = resolveTeamTrackingContext(options);
  if (!context.room_id) return null;
  return appendOidForTeamEdition({
    room_id: context.room_id,
    project_id: context.project_id,
    card_id: normalizeTeamTrackValue(options.cardId),
    position_id: String(Math.max(0, options.positionId)),
    tab_id: options.tabId,
    tab_name: options.tabName,
    node_name: normalizeTeamTrackValue(options.nodeName),
    node_num: normalizeTeamTrackValue(options.nodeNum ?? 1) || '1',
    author_id: normalizeTeamTrackValue(options.authorId),
    media_type: options.mediaType,
    media_url: normalizeTeamTrackValue(options.mediaUrl),
  }, context.oid);
}

/** 团队历史记录卡片曝光（曝光一次，上报一次）。 */
export function trackTeamHistoryCardShow(options: TrackTeamHistoryCardOptions): void {
  const payload = buildTeamHistoryCardTrackPayload(options);
  if (payload) mtstatTrack('roboneo_team_history_card_show', payload);
}

/** 团队历史记录卡片点击。 */
export function trackTeamHistoryCardClick(options: TrackTeamHistoryCardOptions): void {
  const payload = buildTeamHistoryCardTrackPayload(options);
  if (payload) mtstatTrack('roboneo_team_history_card_click', payload);
}

/** 创建团队成功后上报。 */
export function trackTeamOidCreate({
  oid,
  oidName,
  teamNum,
}: TrackTeamOidCreateOptions): void {
  mtstatTrack('roboneo_team_oid_create', {
    oid_name: oidName,
    team_num: String(teamNum),
    oid: String(oid),
  });
}

/** 团队项目页点击“从个人项目导入”入口时上报。 */
export function trackTeamImportClick(): void {
  const context = resolveTeamTrackingContext();
  if (!isTeamEditionTracking() || !context.oid) return;
  mtstatTrack('roboneo_team_import_click', {
    project_id: context.project_id,
    room_id: context.room_id,
    oid: context.oid,
  });
}

/** 将项目弹窗列表字段按展示顺序拼成埋点字符串。 */
function joinTeamUploadTrackValues(
  values: Array<string | number | null | undefined>,
): string {
  return values
    .map(normalizeTeamTrackValue)
    .filter(Boolean)
    .join(',');
}

/** 上报从个人项目导入弹窗曝光，并携带本次展示的项目内容。 */
export function trackTeamUploadDialogShow({
  projectIds,
  roomIds,
  urls,
  oid,
}: TrackTeamUploadDialogOptions): void {
  const context = resolveTeamTrackingContext({ oid });
  if (!isTeamEditionTracking() || !context.oid) return;
  mtstatTrack('roboneo_team_upload_pop_show', {
    room_id: joinTeamUploadTrackValues(roomIds),
    project_id: joinTeamUploadTrackValues(projectIds),
    oid: context.oid,
    url_list: joinTeamUploadTrackValues(urls),
  });
}

/** 上报从个人项目导入确认，并携带用户实际选择的项目内容。 */
export function trackTeamUploadDialogConfirm({
  projectIds,
  roomIds,
  urls,
  oid,
}: TrackTeamUploadDialogOptions): void {
  const context = resolveTeamTrackingContext({ oid });
  if (!isTeamEditionTracking() || !context.oid) return;
  mtstatTrack('roboneo_team_upload_pop_confirm', {
    room_id: joinTeamUploadTrackValues(roomIds),
    project_id: joinTeamUploadTrackValues(projectIds),
    oid: context.oid,
    url_list: joinTeamUploadTrackValues(urls),
  });
}

/** 画布删除接口返回后上报成功或失败状态。 */
export function trackTeamCanvasDeleted({
  projectId,
  roomId,
  location,
  state,
}: {
  projectId: string | number;
  roomId: string | number;
  location: 'team_project' | 'room_bar_more';
  state: 'success' | 'fail';
}): void {
  const context = resolveTeamTrackingContext({ projectId, roomId });
  if (!context.project_id || !context.room_id) return;
  mtstatTrack('roboneo_canvas_delete', appendOidForTeamEdition({
    project_id: context.project_id,
    room_id: context.room_id,
    state,
    location,
  }, context.oid));
}
